import { buildMonthResolver } from './month-resolver.js';
import { buildDatePatterns } from './pattern-builders.js';
import { processResult, processMonthYear } from './processors.js';
import { normY, escRx } from './date-utils.js';

// Debug mode from URL parameter
const debugMode = typeof window !== 'undefined' && window.location && window.location.search.includes('debug=1') ? '1' : '0';

// Track last recognized query for deduplication
let lastRecognizedQuery = '';

export function recognizeDate(query) {
    if (!query || typeof query !== 'string') return null;
    
    const q = query.trim();
    if (!q) return null;
    
    // Build month resolver
    const { monthRxStr, resolveMonth } = buildMonthResolver();
    
    // Build regex patterns
    const patterns = buildDatePatterns(monthRxStr);
    
    // Initialize state
    const state = {
        q,
        dateFrom: null,
        dateTo: null,
        dateVariants: [],
        usedTokens: [],
        stripped: q
    };
    
    // Try all patterns grouped by priority
    // Group 1: Full dates (3 components: day, month, year) - HIGHEST PRIORITY
    // Group 2: Partial dates (2 components: day+month OR month+year) - MEDIUM PRIORITY
    // Group 3: Single component (year only OR day only) - LOWEST PRIORITY
    let dateProcessed = false;
    let mMatch;
    let group1Found = false; // Track if Group 1 found any matches
    
    // ==========================================
    // GROUP 1: FULL DATES (3 COMPONENTS)
    // ==========================================
    // Check all patterns with 3 components first
    // If ANY match found, process all matches and stop (don't check Group 2 or Group 3)
    
    // 1. ISO format (YYYY-MM-DD)
    if ((mMatch = q.match(patterns.isoDateRx))) {
        const matchedStr = mMatch[0];
        const year = parseInt(mMatch[1], 10);
        const month = parseInt(mMatch[2], 10);
        const day = parseInt(mMatch[3], 10);
        if (year >= 1900 && year <= 2099 && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
            processResult(day, month, year, matchedStr, false, state);
            group1Found = true;
        }
    }
    
    // 2. Day Month Year (text month)
    let dmyMatched = false;
    if (!group1Found && (mMatch = q.match(patterns.dayMonthYearRx))) {
        const day = parseInt(mMatch[1], 10);
        if (day > 31) {
            // Skip, might be YMD format
        } else {
            dmyMatched = true;
            if (debugMode === '1') console.log('[Smart-Date-Recognizer] Pattern 2 (DMY text) matched:', mMatch[0]);
            const matchedStr = mMatch[0];
            const matchIndex = mMatch.index;
            const matchEnd = matchIndex + matchedStr.length;
            let actualMatchEnd = matchEnd;
            if (matchEnd < q.length && /[\s\-\.,/]/.test(q[matchEnd])) {
                actualMatchEnd = matchEnd + 1;
            }
            const fullMatchedStr = q.substring(matchIndex, actualMatchEnd);
            
            const monthMatched = mMatch[2].toLowerCase();
            const month = resolveMonth(monthMatched);
            const yearStr = mMatch[3];
            const year = yearStr ? normY(yearStr) : null;
            
            if (month) {
                const isAmbiguous = yearStr && 
                                   yearStr.length === 2 &&
                                   parseInt(yearStr, 10) <= 31 &&
                                   day !== parseInt(yearStr, 10);
                
                if (isAmbiguous) {
                    processResult(day, month, year, fullMatchedStr, false, state);
                    const variantYear = normY(String(day));
                    const variantDay = parseInt(yearStr, 10);
                    if (variantDay >= 1 && variantDay <= 31 && variantYear) {
                        processResult(variantDay, month, variantYear, fullMatchedStr, true, state);
                    }
                } else {
                    processResult(day, month, year, fullMatchedStr, false, state);
                }
                group1Found = true;
            }
        }
    }
    
    // 3. Year Month Day (text month)
    if (!group1Found && !dmyMatched && (mMatch = q.match(patterns.yearMonthDayTextRx))) {
        if (debugMode === '1') console.log('[Smart-Date-Recognizer] Pattern 3 (YMD text) matched:', mMatch[0]);
        const matchedStr = mMatch[0];
        const year = normY(mMatch[1]);
        const monthMatched = mMatch[2].toLowerCase();
        const month = resolveMonth(monthMatched);
        const day = parseInt(mMatch[3], 10);
        if (day <= 31 && month && year) {
            processResult(day, month, year, matchedStr, false, state);
            group1Found = true;
        }
    }
    
    // 4. Month Day Year (text month) - only if year is present
    if (!group1Found && (mMatch = q.match(patterns.monthDayYearRx))) {
        const dayStr = mMatch[2];
        const yearStr = mMatch[3];
        const day = parseInt(dayStr, 10);
        const matchedStr = mMatch[0];
        
        // Check if day+year form a 4-digit year WITHOUT separator between them
        // e.g., "nov 1972" → regex captures day=19, year=72, but "1972" is ONE number (no separator)
        // vs "nov 20 02" → regex captures day=20, year=02, with " " separator between them
        let isActuallyMonthYear = false;
        if (yearStr && dayStr.length === 2 && yearStr.length === 2) {
            const combined = parseInt(dayStr + yearStr, 10);
            if (combined >= 1900 && combined <= 2099) {
                // Check if there's a separator between day and year in the matched string
                const dayEndIdx = matchedStr.lastIndexOf(dayStr) + dayStr.length;
                const yearStartIdx = matchedStr.lastIndexOf(yearStr);
                const gap = matchedStr.substring(dayEndIdx, yearStartIdx);
                
                // If no separator between day and year, it's actually a 4-digit year
                if (gap === '') {
                    isActuallyMonthYear = true;
                    if (debugMode === '1') console.log('[Smart-Date-Recognizer] Pattern 6 (MDY text) skipped - detected as Month+Year:', dayStr + yearStr);
                }
            }
        }
        
        if (!isActuallyMonthYear && day <= 31) {
            const monthMatched = mMatch[1].toLowerCase();
            const month = resolveMonth(monthMatched);
            const year = yearStr ? normY(yearStr) : null;
            if (month && year) {
                if (debugMode === '1') console.log('[Smart-Date-Recognizer] Pattern 6 (MDY text) matched:', matchedStr);
                processResult(day, month, year, matchedStr, false, state);
                group1Found = true;
            }
        }
    }
    
    // 5. Year Month Day (numeric)
    if (!group1Found && (mMatch = q.match(patterns.yearMonthDayRx))) {
        const day = parseInt(mMatch[3], 10);
        if (day <= 31) {
            if (debugMode === '1') console.log('[Smart-Date-Recognizer] Pattern 7 (YMD numeric) matched:', mMatch[0]);
            const matchedStr = mMatch[0];
            const year = parseInt(mMatch[1], 10);
            const month = parseInt(mMatch[2], 10);
            if (year >= 1900 && year <= 2099 && month >= 1 && month <= 12) {
                processResult(day, month, year, matchedStr, false, state);
                group1Found = true;
            }
        }
    }
    
    // 6. Numeric dates with 3 components (DD.MM.YYYY, DD-MM-YY, YY.MM.DD, etc.)
    if (!group1Found && (mMatch = q.match(patterns.numericDateRx))) {
        const num3 = mMatch[3] ? parseInt(mMatch[3], 10) : null;
        // Only process if all 3 components are present
        if (num3 !== null) {
            if (debugMode === '1') console.log('[Smart-Date-Recognizer] Pattern 6 (numericDateRx 3-component) matched:', mMatch[0]);
            const matchedStr = mMatch[0].trim();
            const num1 = parseInt(mMatch[1], 10);
            const num2 = parseInt(mMatch[2], 10);
            
            const isAmbiguous = num1 <= 31 && num2 >= 1 && num2 <= 12 && 
                               num3 <= 31 && num2 >= 1 && num2 <= 12 &&
                               num1 !== num3;
            
            if (isAmbiguous) {
                const day1 = num1;
                const month1 = num2;
                const year1 = normY(String(num3));
                if (day1 >= 1 && day1 <= 31 && month1 >= 1 && month1 <= 12 && year1) {
                    processResult(day1, month1, year1, matchedStr, false, state);
                }
                const year2 = normY(String(num1));
                const month2 = num2;
                const day2 = num3;
                if (day2 >= 1 && day2 <= 31 && month2 >= 1 && month2 <= 12 && year2) {
                    processResult(day2, month2, year2, matchedStr, true, state);
                }
            } else {
                let day = num1;
                let month = num2;
                const year = normY(String(num3));
                
                // Check for YY.MM.DD format FIRST
                if (num1 > 31) {
                    const yearCandidate = normY(String(num1));
                    if (yearCandidate && num2 >= 1 && num2 <= 12 && num3 >= 1 && num3 <= 31) {
                        processResult(num3, num2, yearCandidate, matchedStr, false, state);
                        group1Found = true;
                    }
                }
                
                // If YY.MM.DD didn't match, try YYDDMM format
                if (!group1Found && year && num2 > 12 && num2 <= 31 && num3 >= 1 && num3 <= 12) {
                    day = num2;
                    month = num3;
                    processResult(day, month, year, matchedStr, false, state);
                    group1Found = true;
                }
                
                // Standard DMY/MDY interpretation
                if (!group1Found) {
                    if (month > 12 && day <= 12) {
                        [day, month] = [month, day];
                    }
                    if (year) {
                        processResult(day, month, year, matchedStr, false, state);
                        group1Found = true;
                    }
                }
            }
            if (group1Found) {
                dateProcessed = true;
            }
        }
    }
    
    // 7. 8-digit compact (DDMMYYYY, YYYYMMDD)
    if (!group1Found) {
        const compact8Match = q.match(/(?<![\p{L}\p{N}])(\d{8})(?=\s|$|[^\p{L}\p{N}])/iu);
        if (compact8Match) {
            const digits = compact8Match[1];
            const day1 = parseInt(digits.substring(0, 2), 10);
            const month1 = parseInt(digits.substring(2, 4), 10);
            const year1 = parseInt(digits.substring(4, 8), 10);
            
            if (day1 >= 1 && day1 <= 31 && month1 >= 1 && month1 <= 12 && year1 >= 1900 && year1 <= 2099) {
                processResult(day1, month1, year1, digits, false, state);
                group1Found = true;
            } else {
                const year2 = parseInt(digits.substring(0, 4), 10);
                const month2 = parseInt(digits.substring(4, 6), 10);
                const day2 = parseInt(digits.substring(6, 8), 10);
                
                if (day2 >= 1 && day2 <= 31 && month2 >= 1 && month2 <= 12 && year2 >= 1900 && year2 <= 2099) {
                    processResult(day2, month2, year2, digits, false, state);
                    group1Found = true;
                }
            }
        }
    }
    
    // 8. YYMM[A-Z]DD format
    if (!group1Found && (mMatch = q.match(patterns.yearMonthLetterDayRx))) {
        if (debugMode === '1') console.log('[Smart-Date-Recognizer] Pattern 10 (YYMM[A-Z]DD) matched:', mMatch[0]);
        const matchedStr = mMatch[0];
        const year = normY(mMatch[1]);
        const month = parseInt(mMatch[2], 10);
        const day = parseInt(mMatch[4], 10);
        
        if (day >= 1 && day <= 31 && month >= 1 && month <= 12 && year) {
            processResult(day, month, year, matchedStr, false, state);
            group1Found = true;
        }
    }
    
    // 9. 6-digit compact (YYMMDD, DDMMYY, MMDDYY, YYDDMM)
    if (!group1Found) {
        const compact6Match = q.match(/(?:^|(?<![\p{L}\p{N}]))(\d{6})(?=\s|$|[^\p{L}\p{N}])/iu);
        if (compact6Match) {
            const digits = compact6Match[1];
            const interpretations = [];
            
            const year1Short = digits.substring(0, 2);
            const year1 = normY(year1Short);
            const month1 = parseInt(digits.substring(2, 4), 10);
            const day1 = parseInt(digits.substring(4, 6), 10);
            
            if (day1 >= 1 && day1 <= 31 && month1 >= 1 && month1 <= 12 && year1) {
                interpretations.push({ type: 'ymd', day: day1, month: month1, year: year1, priority: 1 });
            }
            
            const dayMiddle = parseInt(digits.substring(2, 4), 10);
            const monthLast = parseInt(digits.substring(4, 6), 10);
            if (dayMiddle > 12 && dayMiddle <= 31 && monthLast >= 1 && monthLast <= 12 && year1) {
                interpretations.push({ type: 'ydm', day: dayMiddle, month: monthLast, year: year1, priority: 1 });
            }
            
            const day2 = parseInt(digits.substring(0, 2), 10);
            const month2 = parseInt(digits.substring(2, 4), 10);
            const year2Short = digits.substring(4, 6);
            const year2 = normY(year2Short);
            
            const dmyValid = day2 >= 1 && day2 <= 31 && month2 >= 1 && month2 <= 12 && year2;
            if (dmyValid) {
                interpretations.push({ type: 'dmy', day: day2, month: month2, year: year2, priority: 2 });
            }
            
            const month3 = parseInt(digits.substring(0, 2), 10);
            const day3 = parseInt(digits.substring(2, 4), 10);
            const year3Short = digits.substring(4, 6);
            const year3 = normY(year3Short);
            
            const mdyValid = month3 >= 1 && month3 <= 12 && day3 >= 1 && day3 <= 31 && year3 && !dmyValid;
            if (mdyValid) {
                interpretations.push({ type: 'mdy', day: day3, month: month3, year: year3, priority: 3 });
            }
            
            if (interpretations.length > 0) {
                if (debugMode === '1') {
                    console.log('[Smart-Date-Recognizer] Pattern 11 (6-digit compact) matched:', digits, 'interpretations:', interpretations.length);
                }
                interpretations.forEach((interp, idx) => {
                    processResult(interp.day, interp.month, interp.year, digits, idx > 0, state);
                });
                state.usedTokens = [digits.substring(0, 2), digits.substring(2, 4), digits.substring(4, 6)];
                const matchIndex = compact6Match.index;
                const matchEnd = matchIndex + digits.length;
                let actualMatchEnd = matchEnd;
                if (matchEnd < q.length && /[\s\-\.,/]/.test(q[matchEnd])) {
                    actualMatchEnd = matchEnd + 1;
                }
                const fullMatchedStr = q.substring(matchIndex, actualMatchEnd);
                state.stripped = q.replace(new RegExp(escRx(fullMatchedStr) + '\\s*', 'iu'), '').replace(/\s+/g, ' ').trim();
                group1Found = true;
            }
        }
    }
    
    // If Group 1 found matches, mark as processed and skip Group 2 and Group 3
    if (group1Found) {
        dateProcessed = true;
    }
    
    // ==========================================
    // GROUP 2: PARTIAL DATES (2 COMPONENTS)
    // ==========================================
    // Only check if Group 1 didn't find anything
    // Check patterns with 2 components: day+month OR month+year
    
    if (!dateProcessed) {
        // 1. Year Month (text month)
        if ((mMatch = q.match(patterns.yearMonthTextRx))) {
            if (debugMode === '1') console.log('[Smart-Date-Recognizer] Pattern 4 (Year Month text) matched:', mMatch[0]);
            const matchedStr = mMatch[0];
            const year = normY(mMatch[1]);
            const monthMatched = mMatch[2].toLowerCase();
            const month = resolveMonth(monthMatched);
            if (month && year) {
                processMonthYear(month, year, matchedStr, false, state);
                dateProcessed = true;
            }
        }
        
        // 2. Month Year (text month)
        if (!dateProcessed && (mMatch = q.match(patterns.monthYearTextRx))) {
            if (debugMode === '1') console.log('[Smart-Date-Recognizer] Pattern 5 (Month Year text) matched:', mMatch[0]);
            const matchedStr = mMatch[0];
            const monthMatched = mMatch[1].toLowerCase();
            const month = resolveMonth(monthMatched);
            const year = normY(mMatch[2]);
            if (month && year) {
                processMonthYear(month, year, matchedStr, false, state);
                dateProcessed = true;
            }
        }
        
        // 3. Month Day Year (text month) - only if NO year (day+month only)
        if (!dateProcessed && (mMatch = q.match(patterns.monthDayYearRx))) {
            const day = parseInt(mMatch[2], 10);
            if (day <= 31) {
                const matchedStr = mMatch[0];
                const monthMatched = mMatch[1].toLowerCase();
                const month = resolveMonth(monthMatched);
                const year = mMatch[3] ? normY(mMatch[3]) : null;
                // Only process if NO year (day+month only)
                if (month && !year) {
                    if (debugMode === '1') console.log('[Smart-Date-Recognizer] Pattern 6 (MDY text, no year) matched:', matchedStr);
                    processResult(day, month, null, matchedStr, false, state);
                    dateProcessed = true;
                }
            }
        }
        
        // 4. Month Year (numeric)
        if (!dateProcessed && (mMatch = q.match(patterns.monthYearNumericRx))) {
            const matchedStr = mMatch[0];
            const num1 = parseInt(mMatch[1], 10);
            const num2 = parseInt(mMatch[2], 10);
            
            // Skip if num1 > 12 (likely year, not month)
            if (num1 <= 12) {
                if (debugMode === '1') console.log('[Smart-Date-Recognizer] Pattern 8 (Month Year numeric) matched:', matchedStr);
                const year2 = normY(String(num2));
                
                const couldBeDayMonth = num1 <= 31 && num2 >= 1 && num2 <= 12;
                const couldBeMonthYear = num1 >= 1 && num1 <= 12 && year2;
                const year1 = normY(String(num1));
                const couldBeYearMonth = num2 >= 1 && num2 <= 12 && year1;
                
                if (couldBeDayMonth && couldBeMonthYear) {
                    processResult(num1, num2, null, matchedStr, false, state);
                    processMonthYear(num1, year2, matchedStr, true, state);
                } else if (couldBeMonthYear) {
                    processMonthYear(num1, year2, matchedStr, false, state);
                } else if (couldBeYearMonth) {
                    processMonthYear(num2, year1, matchedStr, false, state);
                } else if (couldBeDayMonth) {
                    processResult(num1, num2, null, matchedStr, false, state);
                }
                dateProcessed = true;
            }
        }
        
        // 5. Numeric dates with 2 components only (no third component)
        if (!dateProcessed && (mMatch = q.match(patterns.numericDateRx))) {
            const num3 = mMatch[3] ? parseInt(mMatch[3], 10) : null;
            // Only process if NO third component
            if (num3 === null) {
                if (debugMode === '1') console.log('[Smart-Date-Recognizer] Pattern 6 (numericDateRx 2-component) matched:', mMatch[0]);
                const matchedStr = mMatch[0].trim();
                const num1 = parseInt(mMatch[1], 10);
                const num2 = parseInt(mMatch[2], 10);
                
                const couldBeDayMonth = num1 <= 31 && num2 >= 1 && num2 <= 12;
                if (couldBeDayMonth) {
                    processResult(num1, num2, null, matchedStr, false, state);
                    dateProcessed = true;
                }
            }
        }
        
        // 6. 4-digit compact (MMDD, DDMM, MMYY, YYMM)
        if (!dateProcessed) {
            const compact4Match = q.match(/(?:^|(?<![\p{L}\p{N}]))(\d{4})(?![A-Za-z])(?=\s|$|[^\p{L}\p{N}])/iu);
            if (compact4Match) {
                const digits = compact4Match[1];
                const num1 = parseInt(digits.substring(0, 2), 10);
                const num2 = parseInt(digits.substring(2, 4), 10);
                const year1 = normY(digits.substring(0, 2));
                const year2 = normY(digits.substring(2, 4));
                
                const interpretations = [];
                
                if (num1 >= 1 && num1 <= 31 && num2 >= 1 && num2 <= 12) {
                    interpretations.push({ type: 'day-month', day: num1, month: num2, year: null, level: 3 });
                }
                
                if (num1 >= 1 && num1 <= 12 && num2 >= 1 && num2 <= 31 && num1 !== num2) {
                    interpretations.push({ type: 'month-day', day: num2, month: num1, year: null, level: 3 });
                }
                
                if (num1 >= 1 && num1 <= 12 && year2) {
                    interpretations.push({ type: 'month-year', month: num1, year: year2, level: 2 });
                }
                
                if (num2 >= 1 && num2 <= 12 && year1) {
                    interpretations.push({ type: 'year-month', month: num2, year: year1, level: 2 });
                }
                
                if (interpretations.length > 0) {
                    if (debugMode === '1') {
                        console.log('[Smart-Date-Recognizer] Pattern 12 (4-digit compact) matched:', digits, 'interpretations:', interpretations.length);
                    }
                    
                    interpretations.sort((a, b) => a.level - b.level);
                    
                    interpretations.forEach((interp, idx) => {
                        if (interp.type === 'day-month' || interp.type === 'month-day') {
                            processResult(interp.day, interp.month, interp.year, digits, idx > 0, state);
                        } else if (interp.type === 'month-year' || interp.type === 'year-month') {
                            processMonthYear(interp.month, interp.year, digits, idx > 0, state);
                        }
                    });
                    
                    state.usedTokens = [digits.substring(0, 2), digits.substring(2, 4)];
                    const matchIndex = compact4Match.index;
                    const matchEnd = matchIndex + digits.length;
                    let actualMatchEnd = matchEnd;
                    if (matchEnd < q.length && /[\s\-\.,/]/.test(q[matchEnd])) {
                        actualMatchEnd = matchEnd + 1;
                    }
                    const fullMatchedStr = q.substring(matchIndex, actualMatchEnd);
                    state.stripped = q.replace(new RegExp(escRx(fullMatchedStr) + '\\s*', 'iu'), '').replace(/\s+/g, ' ').trim();
                    dateProcessed = true;
                }
            }
        }
    }
    
    // ==========================================
    // GROUP 3: SINGLE COMPONENT (YEAR ONLY OR DAY ONLY)
    // ==========================================
    // Only check if Group 1 and Group 2 didn't find anything
    // This would be patterns that match only a year or only a day
    // Currently not implemented, but structure is ready
    
    // If no date found, return null
    if (!state.dateFrom && !state.dateTo && state.dateVariants.length === 0) {
        return null;
    }
    
    const result = {
        from: state.dateFrom,
        to: state.dateTo,
        variants: state.dateVariants.length > 0 ? state.dateVariants : null,
        usedTokens: state.usedTokens,
        strippedQuery: state.stripped,
        query: q
    };
    
    // Deduplication: only log if query changed
    const queryChanged = q && q !== lastRecognizedQuery;
    if (debugMode === '1' && queryChanged) {
        console.log('🧠 [Smart-Date-Recognizer] Recognized date:', {
            query: q,
            dateFrom: state.dateFrom,
            dateTo: state.dateTo,
            variants: state.dateVariants.length,
            usedTokens: state.usedTokens,
            strippedQuery: state.stripped
        });
        lastRecognizedQuery = q;
    }
    
    return result;
}

// Export for global window object (backward compatibility)
window.recognizeDate = recognizeDate;
